<template>
  <v-container fluid>
    <HeaderBar>
      <v-icon slot="icon">{{ isEdit ? 'edit' : 'add_box' }}</v-icon>
      {{ isEdit ? 'Edit Event' : 'Create Event' }}
    </HeaderBar>

    <v-layout column>
      <v-flex v-if="loadError">
        <v-alert :value="true" type="error">Couldn't load that event!</v-alert>
      </v-flex>

      <v-flex>
        <v-form v-model="valid" ref="form">
          <v-layout row wrap>
            <v-flex grow>
              <v-text-field
              v-model="event.title"
              label="Event Title"
              :rules="[(v) => !!v || 'Title is required']"
              required
              class="required"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex grow>
              <ShiftScheduleSelector
                :start="event.start"
                :end="event.end"
                @updateStart="(v) => { event.start = v }"
                @updateEnd="(v) => { event.end = v }"
                :overrideSuggestedEnd="1"
                overrideStartLabel="Event Start"
                overrideEndLabel="Event End"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex grow>
              <v-textarea
              v-model="event.description"
              label="Description"
              required
              class="required"
              :rules="[(v) => !!v || 'Description is required']"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex grow>
              <v-textarea v-model="event.location" label="Location" />
            </v-flex>
          </v-layout>

          <v-layout row>
            <v-switch
              v-model="event.clockable"
              label="Clock in required?"
            />
          </v-layout>

          <v-layout row wrap>
            <v-flex grow>
              <v-autocomplete
                multiple
                v-model="selectedGuestIds"
                label="Guest List"
                :items="selectableGuests"
                item-text="name"
                item-value="id"
              />
            </v-flex>
          </v-layout>
        </v-form>
      </v-flex>

      <v-flex v-if="lockedGuestWarning">
        <v-alert :value="true" type="warning" outline>
          {{ lockedGuestWarning }}
        </v-alert>
      </v-flex>

      <v-flex>
        <v-btn @click="save" block :disabled="!valid || loading" :loading="loading" color="success">
          {{ isEdit ? 'Save Changes' : 'Save &amp; Create' }}
        </v-btn>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import ShiftScheduleSelector from '../service/shift-schedule-selector.vue';
import eventApi from '@/api/event';
import collectionsApi from '@/api/collections';

export default {
  components: {
    ShiftScheduleSelector
  },
  created () {
    this.eventId = this.$route.params.eventId || null
    this.selectableGuestsLoading = true
    collectionsApi.getEmployees(employees => {
      this.selectableGuests = employees
        .filter((e) => !e.deactivated)
        .map((e) => ({ id: e.id, name: e.name }))
      this.selectableGuestsLoading = false
    })
    if (this.isEdit) {
      this.loadEvent()
    }
  },
  data: function () {
    return {
      eventId: null,
      selectableGuestsLoading: false,
      selectableGuests: [],
      loading: false,
      loadError: false,
      valid: false,
      lockedGuestWarning: null,
      // selectedGuestIds is the autocomplete model (array of user ids)
      selectedGuestIds: [],
      // originalRsvps: [{ id, user_id, clock_in }] populated when editing
      originalRsvps: [],
      event: {
        start: null,
        end: null,
        title: '',
        description: '',
        location: '',
        clockable: false,
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.eventId
    },
  },
  methods: {
    loadEvent() {
      this.loading = true
      this.loadError = false
      eventApi.get(this.eventId)
        .then((r) => {
          const data = r.data
          this.event.title = data.title
          this.event.description = data.description
          this.event.location = data.location
          this.event.start = data.start ? new Date(data.start) : null
          this.event.end = data.end ? new Date(data.end) : null
          this.event.clockable = data.clockable
          const rsvps = data.event_rsvps || []
          this.originalRsvps = rsvps.map((rsvp) => ({
            id: rsvp.id,
            user_id: rsvp.user_id,
            clock_in: rsvp.clock_in,
          }))
          this.selectedGuestIds = this.originalRsvps.map((r) => r.user_id)
          // Vuetify 1.5 only recomputes `valid` from user input events; trigger a
          // validation pass so the Save button enables after programmatic field set.
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.validate()
          })
        })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    buildRsvpsAttributes() {
      // Returns { attributes, lockedNames }
      // - kept: { id, user_id }
      // - added: { user_id }
      // - removed (no clock_in): { id, _destroy: true }
      // - removed (has clock_in): filtered out, name added to lockedNames
      const selectedSet = new Set(this.selectedGuestIds)
      const originalByUserId = new Map(this.originalRsvps.map((r) => [r.user_id, r]))
      const guestNameById = new Map(this.selectableGuests.map((g) => [g.id, g.name]))

      const attributes = []
      const lockedNames = []

      // kept and removed cases (iterate originals)
      this.originalRsvps.forEach((rsvp) => {
        if (selectedSet.has(rsvp.user_id)) {
          attributes.push({ id: rsvp.id, user_id: rsvp.user_id })
        } else if (rsvp.clock_in) {
          lockedNames.push(guestNameById.get(rsvp.user_id) || `User ${rsvp.user_id}`)
        } else {
          attributes.push({ id: rsvp.id, _destroy: true })
        }
      })

      // added cases (iterate selected ids without an existing rsvp)
      this.selectedGuestIds.forEach((userId) => {
        if (!originalByUserId.has(userId)) {
          attributes.push({ user_id: userId })
        }
      })

      return { attributes, lockedNames }
    },
    save() {
      if (this.isEdit) {
        this.updateEvent()
      } else {
        this.createEvent()
      }
    },
    createEvent() {
      this.loading = true
      // For create, the autocomplete holds user ids; turn them into nested attrs
      const payload = {
        ...this.event,
        event_rsvps_attributes: this.selectedGuestIds.map((userId) => ({ user_id: userId })),
      }
      eventApi.create(payload)
        .then((r) => {
          const eventResponse = r.data
          this.$store.dispatch('global/showSnackbarSuccess', { message: 'Event saved' })
          this.$router.push({ name: "eventDetail", params: { eventId: eventResponse.id }})
        })
        .finally(() => { this.loading = false })
    },
    updateEvent() {
      this.loading = true
      const { attributes, lockedNames } = this.buildRsvpsAttributes()

      if (lockedNames.length > 0) {
        this.lockedGuestWarning =
          `Can't remove ${lockedNames.join(', ')} — already clocked in. ` +
          `They will stay on the guest list.`
        // Re-select the locked users so the autocomplete reflects what will actually be saved.
        const lockedUserIds = this.originalRsvps
          .filter((r) => r.clock_in && !this.selectedGuestIds.includes(r.user_id))
          .map((r) => r.user_id)
        this.selectedGuestIds = [...this.selectedGuestIds, ...lockedUserIds]
      } else {
        this.lockedGuestWarning = null
      }

      const payload = { ...this.event, event_rsvps_attributes: attributes }

      eventApi.update(this.eventId, payload)
        .then(() => {
          this.$store.dispatch('global/showSnackbarSuccess', { message: 'Event saved' })
          this.$router.push({ name: "eventDetail", params: { eventId: this.eventId }})
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>
